export type CanonicalStatus =
  | 'Draft'
  | 'Classify'
  | 'Classifying'
  | 'Confirm'
  | 'Ready'
  | 'Processing'
  | 'Complete'
  // phase-fix-submission-status-truth — post-submission states. 'Complete'
  // now means "compiled/packaged, NOT yet transmitted"; 'Submitted' is the
  // real transmit record; 'Approved'/'Rejected' come from ESG ack processing.
  | 'Submitted'
  | 'Approved'
  | 'Rejected'
  | 'Failed';

export interface StatusConfigEntry {
  readonly label: string;
  readonly badgeClass: string;
  readonly headerStatusText: string;
}

// Legacy V1 STATUS_CONFIG — ported verbatim (TitleCase keys, lowercase
// .badge-* classes that already live in web/src/index.css).
export const STATUS_CONFIG: Readonly<Record<CanonicalStatus, StatusConfigEntry>> = Object.freeze({
  Draft: {
    label: 'Draft',
    badgeClass: 'badge-draft',
    headerStatusText: 'No documents uploaded yet',
  },
  Classify: {
    label: 'Classify',
    badgeClass: 'badge-classify',
    headerStatusText: 'Documents need classification',
  },
  Classifying: {
    label: 'Classifying',
    badgeClass: 'badge-classifying',
    headerStatusText: 'AI classification in progress',
  },
  Confirm: {
    label: 'Confirm',
    badgeClass: 'badge-confirm',
    headerStatusText: 'Review and confirm classifications',
  },
  Ready: {
    label: 'Ready for validation',
    badgeClass: 'badge-ready',
    headerStatusText: 'Documents confirmed — validate before compiling',
  },
  Processing: {
    label: 'Processing',
    badgeClass: 'badge-processing',
    headerStatusText: 'Submission compilation in progress',
  },
  Complete: {
    label: 'Compiled',
    badgeClass: 'badge-complete',
    // phase-fix-submission-status-truth — compiled/packaged but NOT yet
    // transmitted. "Submitted to Agency" is reserved for a real transmit.
    headerStatusText: 'Compiled — ready to submit to agency',
  },
  Submitted: {
    label: 'Submitted',
    badgeClass: 'badge-complete',
    headerStatusText: 'Submitted to agency — awaiting acknowledgment',
  },
  Approved: {
    label: 'Approved',
    badgeClass: 'badge-complete',
    headerStatusText: 'Accepted by agency',
  },
  Rejected: {
    label: 'Rejected',
    badgeClass: 'badge-failed',
    headerStatusText: 'Rejected by agency',
  },
  Failed: {
    label: 'Failed',
    badgeClass: 'badge-failed',
    headerStatusText: 'Submission compilation failed',
  },
});

// Legacy Dataverse phrases the V1 codebase still produces in some paths.
const STATUS_COMPAT: Readonly<Record<string, CanonicalStatus>> = Object.freeze({
  'Documents Uploaded': 'Classify',
  'Pending Classification': 'Classify',
  'Review Required': 'Confirm',
  'Pending Approval': 'Confirm',
  'Ready to Package': 'Ready',
});

export function normalizeStatus(status: string | null | undefined): string {
  if (!status) return 'Draft';
  return STATUS_COMPAT[status] ?? status;
}

export function getStatusConfig(status: string | null | undefined): StatusConfigEntry {
  const normalized = normalizeStatus(status);
  return STATUS_CONFIG[normalized as CanonicalStatus] ?? STATUS_CONFIG.Draft;
}

// phase-fix-submission-status-truth — the client mirror of the server's
// isSubmissionLocked() (src/services/submissionStatus.ts). A locked sequence
// hides/disables its Edit affordances (Submission Details + Compiler); the
// server rejects edits to locked sequences regardless, this is just the UI.
const LOCKED_STATUSES: ReadonlySet<string> = new Set([
  'Complete',
  'Published',
  'Submitted',
  'Approved',
  'Rejected',
  'PendingAgencyConfirmation',
]);

export function isSequenceLocked(submission: {
  Status?: string | null;
  IsLocked?: boolean | number | null;
}): boolean {
  if (submission.IsLocked === true || submission.IsLocked === 1) return true;
  const s = submission.Status;
  return s != null && LOCKED_STATUSES.has(s);
}

/** The tooltip shown on a disabled Edit control for a locked sequence. */
export const LOCKED_EDIT_TOOLTIP = 'Locked — unlock the sequence to make changes';

// Regulatory-action + document-classification badge map — same set V1 uses
// when the status isn't one of the workflow keys above.
export const EXTRA_BADGE_CLASS: Readonly<Record<string, string>> = Object.freeze({
  Pending: 'badge-processing',
  Approved: 'badge-complete',
  'Complete Response': 'badge-review',
  'Refuse to File': 'badge-failed',
  Withdrawn: 'badge-draft',
  'Tentative Approval': 'badge-review',
  Imported: 'badge-imported',
  Uploaded: 'badge-draft',
  Classified: 'badge-classify',
  Confirmed: 'badge-complete',
});
